import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AppShell, AdminShell } from "@/components/app/Shells";
import Home from "@/pages/Home";
import LegalPage from "@/pages/LegalPage";
import ServicePage from "@/pages/ServicePage";
import MitivisePage from "@/pages/MitivisePage";
import Tsm4Page from "@/pages/Tsm4Page";
import ScreeningPage from "@/pages/ScreeningPage";
import DrugTestingPage from "@/pages/DrugTestingPage";
import FingerprintingPage from "@/pages/FingerprintingPage";
import WorkshopsPage from "@/pages/WorkshopsPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AssessmentPage from "@/pages/compliance/AssessmentPage";
import { Login, Register, ForgotPassword, ResetPassword, RequireAuth } from "@/pages/AuthPages";
import PaymentResult from "@/pages/PaymentResult";
import Dashboard from "@/pages/app/Dashboard";
import Onboarding from "@/pages/app/Onboarding";
import Risks from "@/pages/app/Risks";
import Audits from "@/pages/app/Audits";
import SOPs from "@/pages/app/SOPs";
import Documents from "@/pages/app/Documents";
import Services from "@/pages/app/Services";
import Toolkit from "@/pages/app/Toolkit";
import Billing from "@/pages/app/Billing";
import { AdminDashboard, AdminCustomers, AdminRequests, AdminConfig, AdminOrders } from "@/pages/admin/AdminPages";
import { AdminLeads, AdminWorkshops } from "@/pages/admin/AdminLeadsWorkshops";
import DownloadPage from "@/pages/compliance/DownloadPage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-paper p-6 text-center">
          <div>
            <p className="font-serif text-2xl font-semibold text-navy">Something went wrong.</p>
            <button type="button" onClick={() => window.location.reload()} data-testid="error-reload-button" className="btn-gold mt-6">
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollAndSmooth() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.length <= 1) return;
      const el = document.querySelector(href);
      e.preventDefault();
      if (el) {
        lenis.scrollTo(el, { offset: -88 });
      } else {
        window.location.href = `/${href}`;
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const t = setTimeout(() => {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView();
    }, 350);
    return () => clearTimeout(t);
  }, [pathname]);

  return null;
}

function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-paper font-sans text-ink">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ScrollAndSmooth />
          <Routes>
            <Route path="/" element={<MarketingLayout><Home /></MarketingLayout>} />
            <Route path="/mitivise" element={<MarketingLayout><MitivisePage /></MarketingLayout>} />
            <Route path="/mitivise/assessment" element={<MarketingLayout><AssessmentPage /></MarketingLayout>} />
            <Route path="/tsm4" element={<MarketingLayout><Tsm4Page /></MarketingLayout>} />
            <Route path="/tsm4/background-screening" element={<MarketingLayout><ScreeningPage /></MarketingLayout>} />
            <Route path="/tsm4/drug-testing" element={<MarketingLayout><DrugTestingPage /></MarketingLayout>} />
            <Route path="/tsm4/fingerprinting" element={<MarketingLayout><FingerprintingPage /></MarketingLayout>} />
            <Route path="/workshops" element={<MarketingLayout><WorkshopsPage /></MarketingLayout>} />
            <Route path="/about" element={<MarketingLayout><AboutPage /></MarketingLayout>} />
            <Route path="/contact" element={<MarketingLayout><ContactPage /></MarketingLayout>} />
            <Route path="/services/:slug" element={<MarketingLayout><ServicePage /></MarketingLayout>} />
            {/* Rebrand redirects — legacy public URLs point at the same product under the MITIVISE name */}
            <Route path="/compliance-os" element={<Navigate to="/mitivise" replace />} />
            <Route path="/compliance-os/assessment" element={<Navigate to="/mitivise/assessment" replace />} />
            <Route path="/services/drug-alcohol-testing-services" element={<Navigate to="/tsm4/drug-testing" replace />} />
            <Route path="/services/fingerprinting-background-services" element={<Navigate to="/tsm4/fingerprinting" replace />} />
            <Route path="/privacy" element={<MarketingLayout><LegalPage kind="privacy" /></MarketingLayout>} />
            <Route path="/terms" element={<MarketingLayout><LegalPage kind="terms" /></MarketingLayout>} />
            <Route path="/accessibility" element={<MarketingLayout><LegalPage kind="accessibility" /></MarketingLayout>} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult canceled />} />
            <Route path="/download/toolkit" element={<DownloadPage />} />

            <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="risks" element={<Risks />} />
              <Route path="audits" element={<Audits />} />
              <Route path="sops" element={<SOPs />} />
              <Route path="documents" element={<Documents />} />
              <Route path="services" element={<Services />} />
              <Route path="toolkit" element={<Toolkit />} />
              <Route path="billing" element={<Billing />} />
            </Route>

            <Route path="/admin" element={<RequireAuth admin><AdminShell /></RequireAuth>}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="requests" element={<AdminRequests />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="workshops" element={<AdminWorkshops />} />
              <Route path="config" element={<AdminConfig />} />
            </Route>

            <Route path="*" element={<MarketingLayout><Home /></MarketingLayout>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
